// Team Overview Tab Component

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import type { Team } from '@/types/teams';
import { Shuffle, Users } from 'lucide-react';
import { TEAM_ICONS, getIconByName } from '@/lib/team-icons';
import { TeamIconModal } from './TeamIconModal';
import { TeamColorPicker } from './TeamColorPicker';

interface TeamOverviewTabProps {
  readonly team: Team;
  readonly memberCount?: number;
  readonly isEditMode?: boolean;
  readonly onUpdate?: (updates: Partial<Team>) => void;
  readonly showMetadata?: boolean;
}

export function TeamOverviewTab({
  team,
  memberCount = 0,
  isEditMode = false,
  onUpdate,
  showMetadata = true,
}: Readonly<TeamOverviewTabProps>) {
  const [showIconModal, setShowIconModal] = useState(false);
  
  const IconComponent = getIconByName(team.icon) || Users;
  
  const enabledPermissions = team.permissions?.filter(p => p.granted).length ?? 0;
  const totalPermissions = team.permissions?.length ?? 0;

  const handleChange = (field: string, value: any) => {
    if (onUpdate) {
      onUpdate({ [field]: value });
    }
  };

  const handleRandomIconAndColor = () => {
    // Random icon
    const randomIconIndex = Math.floor(Math.random() * TEAM_ICONS.length);
    const randomIcon = TEAM_ICONS[randomIconIndex].name;
    
    // Random color
    const colors = [
      '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
      '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1',
      '#a855f7', '#d946ef'
    ];
    const randomColorIndex = Math.floor(Math.random() * colors.length);
    const randomColor = colors[randomColorIndex];
    
    // Update both
    if (onUpdate) {
      onUpdate({ icon: randomIcon, color: randomColor });
    }
  };

  return (
    <div className="p-6 space-y-6">
        {/* Team Information and Metadata Cards - Side by Side Full Width */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Information Card */}
          <Card className="border">
            <CardHeader>
              <CardTitle>Team Information</CardTitle>
              <CardDescription>Basic details about this team</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Team Name - First */}
              <div>
                <Label htmlFor="team-name" className="text-sm font-medium mb-2 block">
                  Team Name
                </Label>
                {isEditMode ? (
                  <div className="space-y-2">
                    <Input
                      id="team-name"
                      value={team.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                      placeholder="Enter team name"
                      disabled={team.isDefault && !team.canRename}
                    />
                    {team.isDefault && !team.canRename && (
                      <p className="text-xs text-muted-foreground">
                        Default team names cannot be changed
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold">{team.name}</p>
                    {team.isDefault && (
                      <Badge variant="outline">Default Team</Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Description - Second */}
              <div>
                <Label htmlFor="team-description" className="text-sm font-medium mb-2 block">
                  Description
                </Label>
                {isEditMode ? (
                  <Textarea
                    id="team-description"
                    value={team.description ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange('description', e.target.value)}
                    placeholder="Enter team description"
                    rows={3}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {team.description || 'No description provided'}
                  </p>
                )}
              </div>

              {/* Icon and Color Display - Third - Compact Single Line */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-6 w-6 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${team.color}20`, color: team.color }}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <Label className="text-sm font-medium">Team Icon & Colour</Label>
                  </div>
                  {isEditMode && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRandomIconAndColor}
                      className="h-7 gap-1.5 text-xs"
                    >
                      <Shuffle className="h-3 w-3" />
                      Random
                    </Button>
                  )}
                </div>
                
                {isEditMode ? (
                  <div className="flex items-center gap-3">
                    {/* Icon Button */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowIconModal(true)}
                      className="h-12 px-4 flex items-center gap-2"
                    >
                      <div
                        className="h-8 w-8 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${team.color}20`, color: team.color }}
                      >
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <span className="text-xs">Change Icon</span>
                    </Button>
                    
                    {/* Color Picker - Inline */}
                    <TeamColorPicker
                      value={team.color}
                      onChange={(color) => handleChange('color', color)}
                      compact={true}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div
                      className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${team.color}20`, color: team.color }}
                    >
                      <IconComponent className="h-6 w-6" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-8 w-8 rounded border-2 border-border flex-shrink-0"
                        style={{ backgroundColor: team.color }}
                      />
                      <span className="text-sm font-mono text-muted-foreground">{team.color}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Metadata Card */}
          {showMetadata && (
            <Card className="border">
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
                <CardDescription>Team statistics and information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-sm mt-1">
                      {formatDistanceToNow(new Date(team.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Modified</label>
                    <p className="text-sm mt-1">
                      {(() => {
                        const dateStr = team.updatedAt || team.createdAt;
                        if (!dateStr || dateStr === '0001-01-01T00:00:00' || dateStr === '0001-01-01T00:00:00Z') {
                          return formatDistanceToNow(new Date(team.createdAt), { addSuffix: true });
                        }
                        const date = new Date(dateStr);
                        if (isNaN(date.getTime()) || date.getFullYear() < 1970) {
                          return formatDistanceToNow(new Date(team.createdAt), { addSuffix: true });
                        }
                        return formatDistanceToNow(date, { addSuffix: true });
                      })()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Members</label>
                    <p className="text-sm mt-1">
                      <span className="font-semibold">{memberCount}</span> {memberCount === 1 ? 'member' : 'members'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Permissions</label>
                    <p className="text-sm mt-1">
                      <span className="font-semibold">{enabledPermissions}</span> of {totalPermissions} enabled
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

      {/* Icon Selection Modal */}
      <TeamIconModal
        open={showIconModal}
        onOpenChange={setShowIconModal}
        currentIcon={team.icon}
        currentColor={team.color}
        onSelect={(iconKey) => handleChange('icon', iconKey)}
      />
    </div>
  );
}
